const isNullish = (value) => value === null || value === undefined || value === '';

const UPCART_API_POLL_MS = 200;
const UPCART_API_MAX_WAIT_MS = 30000;

function whenUpcartReady(callback) {
    const deadline = Date.now() + UPCART_API_MAX_WAIT_MS;

    const trySubscribe = () => {
        if (typeof window.upcartSubscribeCartUpdated === 'function') {
            callback();
            return;
        }

        if (Date.now() >= deadline) return;

        setTimeout(trySubscribe, UPCART_API_POLL_MS);
    };

    trySubscribe();
}

class GiftDispatcher extends HTMLElement {
    constructor() {
        super();

        this.gifts = [];
        this.isProcessing = false;
        this.cartUpdatedHandler = this.onCartUpdated.bind(this);
        this.upcartCartUpdatedHandler = () => this.onCartUpdated();
    }

    connectedCallback() {
        if (this.dataset.enabled !== 'true') return;

        this.cart = document.querySelector("cart-notification") || document.querySelector("cart-drawer");
        this.gifts = Array.from(this.querySelectorAll('input[type="hidden"][data-tier-type][data-variant-id]'));
        document.addEventListener('cart:updated', this.cartUpdatedHandler);

        whenUpcartReady(() => {
            window.upcartSubscribeCartUpdated(this.upcartCartUpdatedHandler);

            if (typeof window.upcartSubscribeAddedToCart === 'function') {
                window.upcartSubscribeAddedToCart(this.upcartCartUpdatedHandler);
            }

            if (typeof window.upcartSubscribeItemRemoved === 'function') {
                window.upcartSubscribeItemRemoved(this.upcartCartUpdatedHandler);
            }
        });

        this.validateFromServerCart();
    }

    disconnectedCallback() {
        document.removeEventListener('cart:updated', this.cartUpdatedHandler);
    }

    async onCartUpdated() {
        if (this.isProcessing) return;
        await this.validateFromServerCart();
    }

    async getCart() {
        const cartUrl = `${window.routes.cart_url}.js`; // `${Theme.routes.cart_url}.js`;
        const response = await fetch(cartUrl, { method: 'GET', headers: { 'Content-Type': 'application/json' } });
        return await response.json();
    }

    /**
     * Identifiants de sections à demander à l’API panier — doivent être les mêmes
     * que ceux utilisés par cart.js / renderContents (ex. "cart-drawer", "cart-counter"),
     * pas le data-section-id numérique du DOM.
     */
    getSectionIdsForCartApi() {
        const cartRoot =
            this.cart ||
            document.querySelector('cart-notification') ||
            document.querySelector('cart-drawer');
        if (cartRoot && typeof cartRoot.getSectionsToRender === 'function') {
            const list = cartRoot.getSectionsToRender();
            if (Array.isArray(list) && list.length > 0) {
                return list.map((s) => s.section);
            }
        }
        return [];
    }

    isTierSatisfied(cart, tierType, gift) {
        switch (tierType) {
            case 'cart_items_count': {
                if (isNullish(gift.dataset.cartItemsCount)) return false;
                    const threshold = parseInt(gift.dataset.cartItemsCount, 10);
                    return cart.item_count >= threshold;
                }
            case 'cart_amount': {
                if (isNullish(gift.dataset.cartAmount)) return false;
                    const threshold = parseFloat(gift.dataset.cartAmount);
                    return cart.items_subtotal_price >= threshold;
                }
            case 'product_on_cart': {
                if (isNullish(gift.dataset.productDispatcherId)) return false;
                    const productId = parseInt(gift.dataset.productDispatcherId, 10);
                    return cart.items.some((item) => item.product_id === productId);
                }
            default:
            return false;
        }
    }

    async dispatchCartUpdated(data) {
        let itemCount = data?.item_count;
        if (typeof itemCount !== 'number') {
            const cart = await this.getCart();
            itemCount = cart.item_count;
        }

        if (typeof window.upcartRefreshCart === 'function') {
            window.upcartRefreshCart();
        }

        document.dispatchEvent(
            new CustomEvent('cart:updated', {
                bubbles: true,
                detail: {
                    resource: {},
                    data: {
                        itemCount,
                        source: 'gift-dispatcher',
                        sections: data.sections,
                    },
                },
            }),
        );
    }

    async removeGiftIfPresent(cart, variantId) {
        const lineIndex = cart.items.findIndex((item) => item.variant_id === variantId);
        if (lineIndex === -1) return cart;

        const body = JSON.stringify({
            line: lineIndex + 1,
            quantity: 0,
            sections: this.getSectionIdsForCartApi(),
            sections_url: window.location.pathname,
        });

        const response = await fetch(window.routes.cart_change_url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body,
        });

        const data = await response.json();
        this.applyCartSectionsToDom(data);
        await this.dispatchCartUpdated(data);
        return data;
    }

    async limitLineItemQuantity(cart, variantId, quantity) {
        const lineIndex = cart.items.findIndex((item) => item.variant_id === variantId);
        if (lineIndex === -1) return cart;

        const body = JSON.stringify({
            line: lineIndex + 1,
            quantity,
            sections: this.getSectionIdsForCartApi(),
            sections_url: window.location.pathname,
        });

        const response = await fetch(window.routes.cart_change_url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body,
        });

        const data = await response.json();
        this.applyCartSectionsToDom(data);
        await this.dispatchCartUpdated(data);
        return data;
    }

    applyCartSectionsToDom(data) {
        if (
            this.cart &&
            typeof this.cart.renderContents === 'function' &&
            data &&
            data.sections &&
            !data.errors &&
            typeof window.upcartRefreshCart !== 'function'
        ) {
            // false : ne pas rouvrir le tiroir à chaque synchro cadeau (souvent déclenchée en arrière-plan)
            this.cart.renderContents(data, false);
        }
    }

    async addGift(variantId) {
        const body = JSON.stringify({
            items: [{ id: variantId, quantity: 1 }],
            sections: this.getSectionIdsForCartApi(),
            sections_url: window.location.pathname,
        });

        const response = await fetch(window.routes.cart_add_url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body,
        });

        const data = await response.json();
        this.applyCartSectionsToDom(data);
        await this.dispatchCartUpdated(data);
        return await this.getCart();
    }

    async validateConditions(cart) {
        for (const gift of this.gifts) {
            if (isNullish(gift.dataset.variantId) || isNullish(gift.dataset.tierType)) continue;

            const variantId = parseInt(gift.dataset.variantId, 10);
            const tierType = gift.dataset.tierType;
            const isAvailable = gift.dataset.variantAvailable === 'true';

            if (!isAvailable) {
                cart = await this.removeGiftIfPresent(cart, variantId);
                continue;
            }

            const sufficient = this.isTierSatisfied(cart, tierType, gift);
            if (!sufficient) {
                cart = await this.removeGiftIfPresent(cart, variantId);
                continue;
            }

            const giftLine = cart.items.find((item) => item.variant_id === variantId);
            if (giftLine) {
                if (giftLine.quantity > 1) {
                    cart = await this.limitLineItemQuantity(cart, variantId, 1);
                }
                continue;
            }

            cart = await this.addGift(variantId);
        }

        return cart;
    }

    async validateFromServerCart() {
        this.isProcessing = true;

        try {
            let cart = await this.getCart();
            cart = await this.validateConditions(cart);
            return cart;
        } catch (e) {
            console.error(e);
        } finally {
            this.isProcessing = false;
        }
    }
}

if (!customElements.get('gift-dispatcher')) {
    customElements.define('gift-dispatcher', GiftDispatcher);
}